import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  GearRequest,
  GearRequestTemplate,
  CreateGearRequestTemplatePayload,
  CreateRequestFromTemplatePayload,
  OpsReportResponse,
  PaginatedResponse,
} from '@/lib/types';

const TEMPLATES_API = '/api/v1/gear-request-templates';
const GEAR_REQUESTS_API = '/api/v1/gear-requests';

// ─── Filter Types ───────────────────────────────────────────────────────────

export interface GearRequestTemplateFilters {
  page?: number;
  page_size?: number;
  project_type?: string;
  category?: string;
  is_active?: string;
}

export interface OpsReportFilters {
  category?: string;
  in_transit_days?: number;
  page?: number;
  page_size?: number;
}

function cleanFilters<T extends object>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );
}

// ─── Templates List ─────────────────────────────────────────────────────────

export function useGearRequestTemplates(filters: GearRequestTemplateFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.gearRequestTemplates.list(clean),
    queryFn: async (): Promise<PaginatedResponse<GearRequestTemplate>> => {
      const response = await apiClient.get<PaginatedResponse<GearRequestTemplate>>(
        `${TEMPLATES_API}/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

// ─── Template Detail ────────────────────────────────────────────────────────

export function useGearRequestTemplate(id: number) {
  return useQuery({
    queryKey: queryKeys.gearRequestTemplates.detail(id),
    queryFn: async (): Promise<GearRequestTemplate> => {
      const response = await apiClient.get<GearRequestTemplate>(
        `${TEMPLATES_API}/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

// ─── Create Template ────────────────────────────────────────────────────────

export function useCreateGearRequestTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateGearRequestTemplatePayload): Promise<GearRequestTemplate> => {
      const response = await apiClient.post<GearRequestTemplate>(
        `${TEMPLATES_API}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.gearRequestTemplates.all });
    },
  });
}

// ─── Update Template ────────────────────────────────────────────────────────

export function useUpdateGearRequestTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateGearRequestTemplatePayload>;
    }): Promise<GearRequestTemplate> => {
      const response = await apiClient.patch<GearRequestTemplate>(
        `${TEMPLATES_API}/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.gearRequestTemplates.all });
      qc.invalidateQueries({
        queryKey: queryKeys.gearRequestTemplates.detail(variables.id),
      });
    },
  });
}

// ─── Delete Template ────────────────────────────────────────────────────────

export function useDeleteGearRequestTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${TEMPLATES_API}/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.gearRequestTemplates.all });
    },
  });
}

// ─── Create Request from Template ───────────────────────────────────────────

export function useCreateRequestFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      data,
    }: {
      templateId: number;
      data: CreateRequestFromTemplatePayload;
    }): Promise<GearRequest> => {
      const response = await apiClient.post<GearRequest>(
        `${TEMPLATES_API}/${templateId}/create-request/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
      qc.invalidateQueries({ queryKey: queryKeys.crossModule.all });
    },
  });
}

// ─── Ops Report ─────────────────────────────────────────────────────────────

export function useOpsReport(filters: OpsReportFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.gearRequests.opsReport(clean),
    queryFn: async (): Promise<OpsReportResponse> => {
      const response = await apiClient.get<OpsReportResponse>(
        `${GEAR_REQUESTS_API}/ops-report/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

// ─── CSV Export ─────────────────────────────────────────────────────────────

export function useExportGearRequestsCSV() {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await apiClient.get(`${GEAR_REQUESTS_API}/export/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data as BlobPart]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'gear_requests.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
}
